'use client'

import React, { useCallback } from 'react'

import { ExternalLink, Eye, Sparkles } from 'lucide-react'

import Button from '@/components/common/Button'

import { cleanFileName } from '@/helpers/file'
import { formatStableDate } from '@/helpers/ssr/dates'

import { ContentPanelV2 } from '../ContentPanelV2'
import { FileCardV2 } from '../FileCardV2'
import { getFileTypeDisplayName } from '../transforms'

// ============================================================================
// Types
// ============================================================================

/**
 * Standard file interface used across all opportunity types
 */
interface OpportunityFile {
  id: string
  name: string
  contentType: string | null
  size: number | null
  summary: string | null
  createdAt: string
}

interface OpportunityFileCardProps {
  file: OpportunityFile
}

// ============================================================================
// Component
// ============================================================================

/**
 * OpportunityFileCard - Shared file card component for all opportunity types
 *
 * Displays a file with icon, metadata, view action, and expandable AI summary.
 * Used in Federal Contracts, Grants, SBIR, and SLED opportunity pages.
 *
 * @example
 * ```tsx
 * {data.files.map((file) => (
 *   <OpportunityFileCard key={file.id} file={file} />
 * ))}
 * ```
 */
const OpportunityFileCard: React.FC<OpportunityFileCardProps> = React.memo(
  ({ file }) => {
    const handleView = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation()
        window.open(`/files/${file.id}`, '_blank')
      },
      [file.id],
    )

    return (
      <FileCardV2>
        <FileCardV2.Trigger>
          <FileCardV2.Icon filename={file.name} />
          <div className="flex flex-col gap-0.5 overflow-hidden">
            <FileCardV2.Title title={file.name}>
              {cleanFileName(file.name)}
            </FileCardV2.Title>
            <FileCardV2.Meta>
              <span className="font-medium">
                {getFileTypeDisplayName(file.contentType, file.name)}
              </span>
              <span>
                {file.size ? `${(file.size / 1024).toFixed(0)} KB` : '—'}
              </span>
              <span>{formatStableDate(file.createdAt) ?? '—'}</span>
            </FileCardV2.Meta>
          </div>
          <FileCardV2.Actions>
            {/* Hide View button on mobile in collapsed state */}
            <Button
              variant="outline-compact"
              onClick={handleView}
              className="hidden h-6 gap-1 px-2 text-sm md:flex"
            >
              <span className="relative h-3 w-3">
                <Eye className="absolute inset-0 h-3 w-3 transition-opacity group-hover/file:opacity-0" />
                <ExternalLink className="absolute inset-0 h-3 w-3 opacity-0 transition-opacity group-hover/file:opacity-100" />
              </span>
              View
            </Button>
          </FileCardV2.Actions>
        </FileCardV2.Trigger>
        <FileCardV2.Content>
          <ContentPanelV2 size="compact" variant="highlight">
            <ContentPanelV2.Header>
              <ContentPanelV2.Title>
                <ContentPanelV2.TitleIcon>
                  <Sparkles />
                </ContentPanelV2.TitleIcon>
                <ContentPanelV2.TitleText>AI Summary</ContentPanelV2.TitleText>
              </ContentPanelV2.Title>
            </ContentPanelV2.Header>
            <ContentPanelV2.Content>
              {file.summary || 'No AI summary available for this file.'}
            </ContentPanelV2.Content>
            <ContentPanelV2.Footer className="gap-3">
              <ContentPanelV2.CopyButton />
              {/* View button for mobile - shown in expanded state, uses hover icon */}
              <Button
                variant="outline-compact"
                onClick={handleView}
                className="h-6 gap-1 px-2 text-sm md:hidden"
              >
                <ExternalLink className="h-3 w-3" />
                View
              </Button>
            </ContentPanelV2.Footer>
          </ContentPanelV2>
        </FileCardV2.Content>
      </FileCardV2>
    )
  },
)

OpportunityFileCard.displayName = 'OpportunityFileCard'

export { OpportunityFileCard }
export type { OpportunityFile, OpportunityFileCardProps }
